// What the analysis views look at: a workspace (the current state or a project) and the models in it, chosen by name,
// function or tag, or all of them. Every view (Overview, Actions, People, Systems) and every report reads the same
// scope, so moving between views never changes what is being looked at. Kept for each organisation in this browser,
// so the views open where the user left off. A project is always looked at beside the current state, so each view
// can show how its figures change
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CURRENT_STATE_PROJECT_KEY, Linqset } from '../common/linqset-model';
import { createInsightsFilter, getModelsForFilter, getProjectName, InsightsFilter, MODEL_FUNCTION, ModelDetails, Organisation } from '../common/organisation';

export interface AnalysisScope {
    workspaceKey: string;
    modelKeys: string[];                // chosen by name; with functions and tags empty, every model
    modelFunctions: MODEL_FUNCTION[];
    modelTags: string[];
}

export const wholeOrganisation = (workspaceKey = CURRENT_STATE_PROJECT_KEY): AnalysisScope =>
    ({ workspaceKey, modelKeys: [], modelFunctions: [], modelTags: [] });

// a project's figures are compared with the current state's
export const comparesWithCurrentState = (scope: AnalysisScope) => scope.workspaceKey !== CURRENT_STATE_PROJECT_KEY;

export const isWholeOrganisation = (scope: AnalysisScope) => !scope.modelKeys.length && !scope.modelFunctions.length && !scope.modelTags.length;

export const scopeFromFilter = (filter: InsightsFilter): AnalysisScope => ({
    workspaceKey: filter.workspaceKey || CURRENT_STATE_PROJECT_KEY, modelKeys: [...(filter.modelKeys || [])], modelFunctions: [...(filter.modelFunctions || [])],
    modelTags: [...(filter.modelTags || [])],
});

// what a model or the insights page is showing, as a scope: a model being edited is just that model; the organisation's
// insights are whatever their filter picks
export const scopeForLinqset = (linqset: Linqset): AnalysisScope => {
    if (linqset.isProcessModel() && linqset.model?._key) return { ...wholeOrganisation(linqset.workspaceKey), modelKeys: [linqset.model._key] };
    return { ...scopeFromFilter(linqset.filter), workspaceKey: linqset.workspaceKey || CURRENT_STATE_PROJECT_KEY };
};

// the filter the server and the reports take, for what the scope shows
export const filterForScope = (scope: AnalysisScope, userKey?: string): InsightsFilter => ({
    ...createInsightsFilter(scope.workspaceKey, userKey), modelKeys: [...scope.modelKeys], modelFunctions: [...scope.modelFunctions], modelTags: [...scope.modelTags],
    comparison: comparesWithCurrentState(scope),
});

// the models the scope shows, in the organisation's order
export const modelsInScope = (scope: AnalysisScope, organisation: Organisation): ModelDetails[] =>
    getModelsForFilter(filterForScope(scope), organisation, organisation.projects?.find(p => p._key === scope.workspaceKey))
        .filter(m => !m.deletedDate && (m.projectKey === CURRENT_STATE_PROJECT_KEY || m.projectKey === scope.workspaceKey));

// "Current state", "Current state · 3 models", "Project X · Invoicing"
export const describeScope = (scope: AnalysisScope, organisation: Organisation): string => {
    const workspace = getProjectName(organisation, scope.workspaceKey) || 'Current state';
    if (isWholeOrganisation(scope)) return `${workspace} · whole organisation`;
    const models = modelsInScope(scope, organisation);
    return `${workspace} · ${models.length === 1 ? models[0].name : `${models.length} models`}`;
};

const storageKey = (organisationKey: string) => `linq.analysisScope.${organisationKey}`;
// this session's copy, for when storage is unavailable (a private window, say)
const remembered = new Map<string, AnalysisScope>();

// the scope last used for an organisation in this browser; storage may be unavailable (a private window, say)
export const loadScope = (organisationKey: string): AnalysisScope | undefined => {
    if (remembered.has(organisationKey)) return remembered.get(organisationKey);
    try {
        const stored = JSON.parse(localStorage.getItem(storageKey(organisationKey)) || 'null');
        if (!stored || typeof stored !== 'object' || typeof stored.workspaceKey !== 'string') return undefined;
        const strings = (v: unknown) => Array.isArray(v) ? v.filter((k): k is string => typeof k === 'string') : [];
        return { workspaceKey: stored.workspaceKey, modelKeys: strings(stored.modelKeys), modelFunctions: strings(stored.modelFunctions) as MODEL_FUNCTION[], modelTags: strings(stored.modelTags) };
    } catch {
        return undefined;
    }
};

export const saveScope = (organisationKey: string, scope: AnalysisScope) => {
    remembered.set(organisationKey, scope);
    try {
        localStorage.setItem(storageKey(organisationKey), JSON.stringify(scope));
    } catch {
        // this session still has it
    }
};

@Injectable({ providedIn: 'root' })
export class ScopeService {
    private organisationKey = '';
    private readonly subject = new BehaviorSubject<AnalysisScope>(wholeOrganisation());
    readonly scope$ = this.subject.asObservable();

    get scope(): AnalysisScope {
        return this.subject.value;
    }

    // the scope for an organisation: the one given (from a link, or the workspace looked at elsewhere), which is
    // remembered as the one last used, else the one last used here, else everything
    open(organisationKey: string, scope?: AnalysisScope): AnalysisScope {
        if (organisationKey !== this.organisationKey || scope) {
            this.organisationKey = organisationKey;
            this.subject.next(scope || loadScope(organisationKey) || wholeOrganisation());
            if (scope) saveScope(organisationKey, scope);
        }
        return this.scope;
    }

    set(scope: AnalysisScope) {
        this.subject.next(scope);
        if (this.organisationKey) saveScope(this.organisationKey, scope);
    }

    // narrows the scope to the models given (keeping the workspace), as when a model's row is chosen
    narrowTo(modelKeys: string[]) {
        this.set({ ...this.scope, modelKeys: [...modelKeys], modelFunctions: [], modelTags: [] });
    }

    widen() {
        this.set(wholeOrganisation(this.scope.workspaceKey));
    }
}
